import goalCategoriesConfig from '../config/fitness_profile_goal_categories.json';

/** Sentinel value — not a real category; must not appear in GOAL_CATEGORY_OPTIONS. */
export const GOAL_CATEGORY_UNSET = '__unset__';

export const GOAL_CATEGORY_OPTIONS = goalCategoriesConfig.categories.map((value) => ({
	value,
	label: goalCategoriesConfig.labels[value] || value
}));

export const SINGLETON_GOAL_CATEGORIES = goalCategoriesConfig.singleton_active_categories;

export const WEIGHT_GOAL_CATEGORIES = goalCategoriesConfig.weight_categories;

const MANUAL_PROGRESS_CATEGORIES = goalCategoriesConfig.categories.filter(
	(category) =>
		!goalCategoriesConfig.weight_categories.includes(category) && category !== 'blood_pressure'
);

export function isManualProgressGoal(goal) {
	if (!goal || goal.status === 'archived') return false;
	if (goal.progress_source === 'manual') return true;
	return MANUAL_PROGRESS_CATEGORIES.includes(goal.category);
}

export function labelForGoalCategory(value) {
	return goalCategoriesConfig.labels[value] || value;
}

export function isGoalCategorySelected(category) {
	return goalCategoriesConfig.categories.includes(category);
}

export function emptyGoalFormRow() {
	return {
		id: null,
		outcome: '',
		targetMetric: '',
		targetDate: '',
		category: GOAL_CATEGORY_UNSET,
		status: 'active',
		_destroy: false
	};
}

export function goalToFormRow(goal) {
	const category = isGoalCategorySelected(goal?.category) ? goal.category : GOAL_CATEGORY_UNSET;
	return {
		id: goal?.id ?? null,
		outcome: goal?.outcome || '',
		targetMetric: goal?.target_metric || '',
		targetDate: goal?.target_date || '',
		category,
		status: goal?.status || 'active',
		_destroy: false
	};
}

export function buildGoalsAddPayload(goalRows) {
	return goalRows
		.filter(
			(row) =>
				!row._destroy &&
				!row.id &&
				(row.outcome.trim() ||
					row.targetMetric.trim() ||
					row.targetDate ||
					isGoalCategorySelected(row.category))
		)
		.map((row) => ({
			outcome: row.outcome.trim(),
			target_metric: row.targetMetric.trim(),
			target_date: row.targetDate,
			category: isGoalCategorySelected(row.category) ? row.category : undefined,
			status: row.status || 'active'
		}));
}

export function buildGoalsUpdatePayload(goalRows) {
	return goalRows
		.filter(
			(row) =>
				row._destroy ||
				row.outcome.trim() ||
				row.targetMetric.trim() ||
				row.targetDate ||
				isGoalCategorySelected(row.category)
		)
		.map((row) => {
			const payload = {
				outcome: row.outcome.trim(),
				target_metric: row.targetMetric.trim(),
				target_date: row.targetDate,
				category: isGoalCategorySelected(row.category) ? row.category : undefined,
				status: row.status || 'active'
			};
			if (row.id) payload.id = row.id;
			if (row._destroy) payload._destroy = true;
			return payload;
		});
}

/** @deprecated Use buildGoalsUpdatePayload */
export const buildGoalsSyncPayload = buildGoalsUpdatePayload;

function countActiveSingletonCategories(goalRows, existingGoals = []) {
	const formIds = new Set(goalRows.filter((row) => row.id).map((row) => row.id));
	const counts = new Map();

	existingGoals.forEach((goal) => {
		if (goal.status !== 'active') return;
		if (formIds.has(goal.id)) return;
		if (!SINGLETON_GOAL_CATEGORIES.includes(goal.category)) return;
		counts.set(goal.category, (counts.get(goal.category) || 0) + 1);
	});

	goalRows.forEach((row) => {
		if (row._destroy || row.status !== 'active') return;
		if (!isGoalCategorySelected(row.category)) return;
		if (!SINGLETON_GOAL_CATEGORIES.includes(row.category)) return;
		counts.set(row.category, (counts.get(row.category) || 0) + 1);
	});

	return counts;
}

function validateReadingPrerequisites(goalRows, { hasWeightReading, hasBloodPressureReading }, errors) {
	goalRows.forEach((row, index) => {
		if (row._destroy || !isGoalCategorySelected(row.category)) return;

		if (WEIGHT_GOAL_CATEGORIES.includes(row.category) && !hasWeightReading) {
			errors[`goal_category_${index}`] = 'Log at least one weight before setting a weight goal.';
		}

		if (row.category === 'blood_pressure' && !hasBloodPressureReading) {
			errors[`goal_category_${index}`] =
				'Log at least one blood pressure reading before setting a blood pressure goal.';
		}
	});
}

export function validateGoalRows(
	goalRows,
	{
		today,
		requireAtLeastOne = true,
		existingGoals = [],
		hasWeightReading = false,
		hasBloodPressureReading = false
	} = {}
) {
	const errors = {};
	let completeCount = 0;

	goalRows.forEach((row, index) => {
		if (row._destroy) return;

		const outcome = row.outcome.trim();
		const targetMetric = row.targetMetric.trim();
		const targetDate = row.targetDate;
		const hasAny = outcome || targetMetric || targetDate;
		if (!hasAny) return;

		if (!outcome) errors[`goal_outcome_${index}`] = 'Outcome is required.';
		if (!targetMetric) errors[`goal_target_${index}`] = 'Target is required.';
		if (!targetDate) {
			errors[`goal_target_date_${index}`] = 'Target date is required.';
		} else if (today && targetDate < today) {
			errors[`goal_target_date_${index}`] = 'Target date must be today or later.';
		}
		if (!isGoalCategorySelected(row.category)) {
			errors[`goal_category_${index}`] = 'Select a category.';
		}

		if (
			outcome &&
			targetMetric &&
			targetDate &&
			isGoalCategorySelected(row.category) &&
			(!today || targetDate >= today)
		) {
			completeCount += 1;
		}
	});

	if (requireAtLeastOne && completeCount === 0) {
		errors.goals = 'Add at least one complete goal.';
	}

	validateReadingPrerequisites(
		goalRows,
		{ hasWeightReading, hasBloodPressureReading },
		errors
	);

	const singletonCounts = countActiveSingletonCategories(goalRows, existingGoals);
	SINGLETON_GOAL_CATEGORIES.forEach((category) => {
		if ((singletonCounts.get(category) || 0) > 1) {
			errors.goals = `Only one active ${labelForGoalCategory(category).toLowerCase()} goal allowed.`;
		}
	});

	return errors;
}

export function activeWeightGoal(goals = []) {
	return (
		goals.find(
			(goal) =>
				goal.status === 'active' &&
				(goal.category === 'weight_loss' || goal.category === 'weight_gain') &&
				goal.baseline_pounds != null &&
				goal.target_pounds != null &&
				goal.starts_on &&
				goal.target_date
		) || null
	);
}

export function activeBloodPressureGoal(goals = []) {
	return (
		goals.find(
			(goal) =>
				goal.status === 'active' &&
				goal.category === 'blood_pressure' &&
				goal.baseline_systolic != null &&
				goal.baseline_diastolic != null &&
				goal.target_systolic != null &&
				goal.target_diastolic != null &&
				goal.starts_on &&
				goal.target_date
		) || null
	);
}
